/**
 * RecipientHeader - email-client-style From / To / Cc / Bcc rows that
 * sit ABOVE the reply editor on the agent ticket-detail composer.
 *
 * Was previously a ``<details>`` toggle below the editor that hid
 * the recipients entirely. The user-feedback principle is that an
 * agent writing a reply needs the recipients visible the entire
 * time (it's an email client, not a chat). FreshDesk follows the
 * same pattern; this matches.
 *
 * Layout:
 *   From: Buyist Support <support@buyist.com>            <- readonly
 *   To:   [Requester chip]                       [Bcc]   <- non-removable
 *   Cc:   [chips...] [+ inline add input]      [Clear list]
 *   Bcc:  [chips...] [+ inline add input]   (only when toggled or non-empty)
 *
 * The "Bcc" toggle on the To row is hidden once Bcc has at least one
 * recipient (the row is permanently visible from then on).
 */
import { useState } from "react";

import { RecipientCombobox } from "./RecipientCombobox";

export interface RecipientHeaderProps {
  ticketNumber: number;
  fromName?: string;
  fromEmail?: string;
  // The ticket's requester (email + display name). Used to tag any
  // chip whose address matches the requester with "(Requester)" so
  // the agent knows which address is the ticket's owner.
  requesterEmail: string;
  requesterName: string;
  // Plain lowercased email lists. The composer in
  // TicketDetailAgentPage owns this state; we mirror it through
  // these props. Watchers (the persistent ticket_watchers table) no
  // longer drive the per-reply CC; the composer is the source of
  // truth and the agent endpoint accepts to_emails / cc_emails /
  // bcc_emails on the comment payload directly.
  toEmails: string[];
  ccEmails: string[];
  bccEmails: string[];
  onAddTo: (email: string) => void;
  onRemoveTo: (email: string) => void;
  onAddCc: (email: string) => void;
  onRemoveCc: (email: string) => void;
  // Drops every Cc chip at once. Inbound threads (Fosdick / NexRep /
  // agency chains) routinely arrive with a dozen-plus Cc addresses,
  // and an agent who wants to reply to the requester alone should
  // not have to click each chip's remove glyph. Optional so legacy
  // call sites keep compiling; the link is hidden when absent.
  onClearCc?: (emails: string[]) => void;
  onAddBcc: (email: string) => void;
  onRemoveBcc: (email: string) => void;
  // Editable outbound Subject. Pre-seeded by the parent with
  // ``[#${public_code}] ${ticket.subject}``. The agent can delete
  // the ``[#code]`` prefix; the backend's
  // ``_ensure_ticket_token_in_subject`` restores it before the
  // email actually hits the wire. When the parent does not pass
  // ``subject`` / ``onChangeSubject`` (legacy call sites), the row
  // is omitted entirely.
  subject?: string;
  onChangeSubject?: (next: string) => void;
  isPending?: boolean;
}

interface RecipientChipProps {
  label: string;
  // Non-truncated suffix that always shows after the (possibly
  // truncated) label - e.g. "(Requester)" on the To row. Without
  // this, a long "Name <email>" + "(Requester)" gets truncated as
  // one string and the suffix is the part that gets cut off, which
  // is exactly what the user reported seeing.
  suffix?: string;
  title?: string;
  onRemove?: () => void;
  removeDisabled?: boolean;
  testId?: string;
}

function RecipientChip({
  label,
  suffix,
  title,
  onRemove,
  removeDisabled,
  testId,
}: RecipientChipProps): JSX.Element {
  return (
    <span
      data-testid={testId}
      title={title ?? (suffix ? `${label} ${suffix}` : label)}
      className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-700"
    >
      <span className="max-w-[18rem] truncate">{label}</span>
      {suffix && (
        <span className="shrink-0 text-neutral-500">{suffix}</span>
      )}
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          // Unicode multiplication sign for the remove glyph - the FA
          // ``fa-xmark`` at chip size (text-xs) reliably renders as
          // empty whitespace on this design system, so callers were
          // seeing a present-but-invisible button. Plain Unicode
          // always shows and matches what every other chip / pill UI
          // does on the web.
          className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full text-sm leading-none text-neutral-500 hover:bg-neutral-200 hover:text-red-600 disabled:opacity-50"
          onClick={onRemove}
          disabled={removeDisabled}
        >
          <span aria-hidden="true">&times;</span>
        </button>
      )}
    </span>
  );
}

interface RecipientRowProps {
  label: string;
  ticketNumber: number;
  // The chip column. Plain email strings; the row renders each as
  // a removable chip via the onRemove callback.
  chips: string[];
  onRemove: (email: string) => void;
  onAdd: (email: string) => void;
  isPending?: boolean;
  testId?: string;
  // Right-side affordance, e.g. the "Bcc" toggle on the To row.
  rightSlot?: React.ReactNode;
  // Emails to exclude from the autocomplete dropdown (so the agent
  // can't add a duplicate of an already-chipped recipient).
  excludeFromSuggestions?: readonly string[];
  // For the To row: when a chip matches this email it gets a
  // "(Requester)" suffix and uses ``requesterDisplayName`` as the
  // visible label, mirroring the email-client convention of showing
  // the ticket's owner clearly. ``undefined`` for Cc / Bcc rows.
  requesterEmail?: string;
  requesterName?: string;
}

function RecipientRow({
  label,
  ticketNumber,
  chips,
  onRemove,
  onAdd,
  isPending,
  testId,
  rightSlot,
  excludeFromSuggestions,
  requesterEmail,
  requesterName,
}: RecipientRowProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-2 border-b border-neutral-100 px-3 py-1.5 text-sm last:border-b-0"
    >
      <span className="mt-1 w-16 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {chips.map((email) => {
          const isRequester =
            requesterEmail &&
            email.toLowerCase() === requesterEmail.toLowerCase();
          const chipLabel =
            isRequester && requesterName
              ? `${requesterName} <${email}>`
              : email;
          return (
            <RecipientChip
              key={email}
              label={chipLabel}
              suffix={isRequester ? "(Requester)" : undefined}
              onRemove={() => onRemove(email)}
              removeDisabled={isPending}
              testId={`${testId}-chip`}
            />
          );
        })}
        <RecipientCombobox
          ticketNumber={ticketNumber}
          ariaLabel={`Add ${label}`}
          placeholder={
            chips.length === 0 ? `Add ${label.toLowerCase()} email` : ""
          }
          onCommit={onAdd}
          isPending={isPending}
          excludeEmails={excludeFromSuggestions}
        />
      </div>
      {rightSlot && <div className="ml-2 shrink-0">{rightSlot}</div>}
    </div>
  );
}

export function RecipientHeader({
  ticketNumber,
  fromName = "Buyist Support",
  fromEmail = "support@buyist.com",
  requesterEmail,
  requesterName,
  toEmails,
  ccEmails,
  bccEmails,
  onAddTo,
  onRemoveTo,
  onAddCc,
  onRemoveCc,
  onClearCc,
  onAddBcc,
  onRemoveBcc,
  subject,
  onChangeSubject,
  isPending,
}: RecipientHeaderProps): JSX.Element {
  // BCC row stays hidden until the agent explicitly opens it or one
  // is already on the ticket. Matches FreshDesk and most native mail
  // clients - keeps the common case (no Bcc) visually quiet.
  const [bccOpen, setBccOpen] = useState<boolean>(bccEmails.length > 0);

  // Render the Subject row only when the parent wired it. Earlier
  // call sites that pre-date the editable-subject feature continue
  // to work unchanged (the row is opt-in).
  const showSubject = subject !== undefined && onChangeSubject !== undefined;

  return (
    <div
      data-testid="recipient-header"
      className="rounded-lg border border-neutral-200 bg-white"
    >
      {/* From - readonly */}
      <div className="flex items-baseline gap-2 border-b border-neutral-100 px-3 py-1.5 text-sm">
        <span className="w-16 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-500">
          From
        </span>
        <span className="truncate text-neutral-700">
          {fromName} &lt;{fromEmail}&gt;
        </span>
      </div>

      {/* Subject - editable. Plain text input; the backend's
          ``_ensure_ticket_token_in_subject`` restores the
          ``[#code]`` token at send time if the agent has deleted
          it. The token is structural (used by inbound threading),
          not editorial, so the agent never has to keep it intact
          themselves. */}
      {showSubject && (
        <div
          data-testid="row-subject"
          className="flex items-center gap-2 border-b border-neutral-100 px-3 py-1.5 text-sm"
        >
          <span className="w-16 shrink-0 text-xs font-medium uppercase tracking-wide text-neutral-500">
            Subject
          </span>
          <input
            type="text"
            value={subject}
            onChange={(e) => onChangeSubject!(e.target.value)}
            maxLength={500}
            disabled={isPending}
            placeholder="Subject"
            aria-label="Email subject"
            data-testid="subject-input"
            className="w-full min-w-0 bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      )}

      {/* To - editable. By default it has one chip for the requester
          (marked "(Requester)"). The agent can remove it and add
          different addresses to re-route the reply; the backend
          comment payload carries those addresses as ``to_emails``
          and the outbound email pipeline uses them in place of the
          requester. */}
      <RecipientRow
        label="To"
        ticketNumber={ticketNumber}
        testId="row-to"
        chips={toEmails}
        onAdd={onAddTo}
        onRemove={onRemoveTo}
        isPending={isPending}
        requesterEmail={requesterEmail}
        requesterName={requesterName}
        excludeFromSuggestions={toEmails}
        rightSlot={
          !bccOpen ? (
            <button
              type="button"
              data-testid="bcc-toggle"
              className="text-xs font-medium text-primary-700 hover:text-primary-800"
              onClick={() => setBccOpen(true)}
            >
              Bcc
            </button>
          ) : null
        }
      />

      {/* Cc - always visible */}
      <RecipientRow
        label="Cc"
        ticketNumber={ticketNumber}
        testId="row-cc"
        chips={ccEmails}
        onAdd={onAddCc}
        onRemove={onRemoveCc}
        isPending={isPending}
        // Don't suggest anyone already on the To or Cc lines.
        // (Bcc list isn't excluded - same address legitimately
        // appears on both if the agent explicitly wants it.)
        excludeFromSuggestions={[...toEmails, ...ccEmails]}
        rightSlot={
          onClearCc && ccEmails.length > 0 ? (
            <button
              type="button"
              data-testid="cc-clear"
              className="text-xs font-medium text-primary-700 hover:text-primary-800 disabled:opacity-50"
              onClick={() => onClearCc(ccEmails)}
              disabled={isPending}
            >
              Clear list
            </button>
          ) : null
        }
      />

      {/* Bcc - togglable */}
      {bccOpen && (
        <RecipientRow
          label="Bcc"
          ticketNumber={ticketNumber}
          testId="row-bcc"
          chips={bccEmails}
          onAdd={onAddBcc}
          onRemove={onRemoveBcc}
          isPending={isPending}
          excludeFromSuggestions={[...toEmails, ...bccEmails]}
        />
      )}
    </div>
  );
}

export default RecipientHeader;
